import { type ReactNode } from 'react';
import { usePermission } from '@backstage/plugin-permission-react';
import {
  Header,
  Container,
  Table,
  useTable,
  CellText,
  type ColumnConfig,
  SearchField,
  Flex,
  ButtonLink,
} from '@backstage/ui';
import {
  RiCalendarScheduleLine,
  RiCircleFill,
  RiFileCloseLine,
  RiGlobalLine,
} from '@remixicon/react';
import { useRouteRef } from '@backstage/frontend-plugin-api';
import { BackstageLogoIcon } from '@internal/backstage-plugin-hm-react';
import {
  pulseAdminPermission,
  PulseIntegrationId,
} from '@internal/backstage-plugin-pulse-common';
import {
  PulseIntegrationItem,
  usePulseFactCollectors,
  useIntegrationHealth,
} from '../../hooks';
import { integrationsDetailsSubRouteRef } from '../../routes';
import { PulseListSkeleton } from '../list-skeleton';

const integrationIcons: Record<PulseIntegrationId, ReactNode> = {
  [PulseIntegrationId.CATALOG]: <BackstageLogoIcon height={18} />,
  [PulseIntegrationId.UNPROCESSED]: <RiFileCloseLine size={18} />,
  [PulseIntegrationId.HTTP]: <RiGlobalLine size={18} />,
};

const isKnownIntegrationId = (
  integrationId: string,
): integrationId is PulseIntegrationId => {
  return Object.values(PulseIntegrationId).includes(
    integrationId as PulseIntegrationId,
  );
};

const IntegrationHealthCell = ({
  integrationId,
}: {
  integrationId: string;
}) => {
  const { value: health, loading, error } = useIntegrationHealth(integrationId);

  if (loading) {
    return <CellText title="-" color="secondary" />;
  }

  if (error || !health) {
    return <CellText title="Unknown" color="secondary" />;
  }

  const title = health.isHealthy ? `Healthy` : `Something went wrong`;

  return (
    <CellText
      title={title}
      leadingIcon={
        health.isHealthy ? (
          <RiCircleFill size={14} color="var(--bui-fg-positive)" />
        ) : (
          <RiCircleFill size={14} color="var(--bui-fg-negative)" />
        )
      }
      color={health.isHealthy ? 'primary' : 'secondary'}
    />
  );
};

const DependentChecksCell = ({ integrationId }: { integrationId: string }) => {
  const { value: health, loading, error } = useIntegrationHealth(integrationId);

  if (loading) {
    return <CellText title="-" color="secondary" />;
  }

  if (error || !health) {
    return <CellText title="Unknown" color="secondary" />;
  }

  const title = `${health.relatedChecksCount} checks`;

  return <CellText title={title} />;
};

const createColumns = (
  getDetailsPath: ((params: { id: string }) => string) | undefined,
): ColumnConfig<PulseIntegrationItem>[] => [
  {
    id: 'integrationId',
    label: 'Name',
    isRowHeader: true,
    defaultWidth: '10fr',
    cell: item => (
      <CellText
        title={item.title ?? item.integrationId}
        href={getDetailsPath ? getDetailsPath({ id: item.id }) : undefined}
        leadingIcon={
          isKnownIntegrationId(item.integrationId)
            ? integrationIcons[item.integrationId]
            : undefined
        }
      />
    ),
  },
  {
    id: 'integrationDescription',
    label: 'Description',
    defaultWidth: '38fr',
    cell: item => <CellText title={item.description ?? ''} color="secondary" />,
  },
  {
    id: 'status',
    label: 'Status',
    defaultWidth: '10fr',
    cell: item => <IntegrationHealthCell integrationId={item.integrationId} />,
  },
  {
    id: 'dependentChecks',
    label: 'Dependent Checks',
    defaultWidth: '10fr',
    cell: item => <DependentChecksCell integrationId={item.integrationId} />,
  },
];

type IntegrationsTableProps = {
  data: PulseIntegrationItem[] | undefined;
  error: Error | undefined;
  columns: ColumnConfig<PulseIntegrationItem>[];
};

const IntegrationsTable = ({
  data,
  error,
  columns,
}: IntegrationsTableProps) => {
  const { tableProps, search } = useTable({
    mode: 'complete',
    data: data ?? [],
    searchFn: (items, query) => {
      const lowerQuery = query.toLowerCase();
      return items.filter(
        item =>
          item.integrationId.toLowerCase().includes(lowerQuery) ||
          (item.title ?? '').toLowerCase().includes(lowerQuery) ||
          (item.description ?? '').toLowerCase().includes(lowerQuery),
      );
    },
    paginationOptions: { pageSize: (data ?? []).length || 1 },
  });
  return (
    <Flex direction="column" gap="6">
      <SearchField
        aria-label="Search"
        placeholder="Search..."
        {...search}
        // eslint-disable-next-line jsx-a11y/no-autofocus
        autoFocus
        style={{ maxWidth: '36vw' }}
      />
      <Table
        columnConfig={columns}
        {...tableProps}
        emptyState="No integrations found"
        error={error}
        pagination={{ type: 'none' }}
      />
    </Flex>
  );
};

export const PulseIntegrationsPage = () => {
  const { allowed: isAdmin } = usePermission({
    permission: pulseAdminPermission,
  });
  const { value: integrations, loading, error } = usePulseFactCollectors();
  const getDetailsPath = useRouteRef(integrationsDetailsSubRouteRef);
  const columns = createColumns(getDetailsPath);

  return (
    <>
      <Header
        title="Integrations"
        description="Integrations are the sources Pulse gathers data from. Each one schedules fact collectors that produce the facts checks are evaluated against."
        customActions={
          isAdmin && (
            <ButtonLink
              variant="secondary"
              iconStart={<RiCalendarScheduleLine />}
              href="/devtools/scheduled-tasks"
            >
              Scheduled Tasks
            </ButtonLink>
          )
        }
      />
      <Container>
        {loading ? (
          <PulseListSkeleton />
        ) : (
          <IntegrationsTable
            data={integrations}
            error={error}
            columns={columns}
          />
        )}
      </Container>
    </>
  );
};
